import React, { useState } from 'react';
import { CheckCircleIcon, AlertTriangleIcon, ClockIcon, ArrowLeftIcon, FileTextIcon, CalendarIcon, CheckIcon, XIcon, SendIcon, CreditCardIcon, ShieldIcon, AlertCircleIcon } from 'lucide-react';
import { FileUpload } from '../FileUpload';
export function ComplianceCheck({
  vendor,
  onUpdate,
  setCurrentView,
  locale = 'en-US'
}) {
  const [selectedVendor, setSelectedVendor] = useState(vendor || null);
  const [files, setFiles] = useState([]);
  const [message, setMessage] = useState('');
  // If no vendor is selected, show a list of vendors with compliance issues
  const vendorsWithIssues = [{
    id: 'VEN-2023-002',
    name: 'Dell Technologies',
    status: 'Active',
    complianceStatus: 'Pending Review',
    type: 'IT Equipment',
    country: 'United States',
    onboardingDate: '2023-02-10',
    lastUpdated: '2023-10-05',
    issue: 'Insurance Certificate expired on 2023-10-10',
    documents: [{
      id: 1,
      name: 'Insurance Certificate',
      type: 'Insurance',
      uploadDate: '2023-02-10',
      expiryDate: '2023-10-10',
      status: 'Expired'
    }],
    bankVerification: {
      status: 'Verified',
      lastChecked: '2023-09-20',
      issues: []
    }
  }, {
    id: 'VEN-2023-003',
    name: 'PrintWorks Inc.',
    status: 'Pending Onboarding',
    complianceStatus: 'Incomplete',
    type: 'Printing Services',
    country: 'Canada',
    onboardingDate: '2023-09-30',
    lastUpdated: '2023-09-30',
    issue: 'Missing required documents',
    documents: [],
    bankVerification: {
      status: 'Not Verified',
      lastChecked: null,
      issues: [{
        severity: 'High',
        description: 'Bank account information not provided'
      }]
    }
  }, {
    id: 'VEN-2023-006',
    name: 'Global Supplies Ltd',
    status: 'Active',
    complianceStatus: 'Non-Compliant',
    type: 'Office Supplies',
    country: 'United Kingdom',
    onboardingDate: '2023-04-15',
    lastUpdated: '2023-10-18',
    issue: "Bank details don't match Eftsure verification",
    documents: [{
      id: 1,
      name: 'Insurance Certificate',
      type: 'Insurance',
      uploadDate: '2023-04-15',
      expiryDate: '2024-04-15',
      status: 'Valid'
    }],
    bankVerification: {
      status: 'Failed',
      lastChecked: '2023-10-18',
      issues: [{
        severity: 'Critical',
        description: "Account name doesn't match registered business name"
      }, {
        severity: 'High',
        description: "BSB/Routing number doesn't match registered bank"
      }]
    }
  }, {
    id: 'VEN-2023-007',
    name: 'TechCore Systems',
    status: 'Active',
    complianceStatus: 'Non-Compliant',
    type: 'IT Services',
    country: 'Australia',
    onboardingDate: '2023-05-22',
    lastUpdated: '2023-10-15',
    issue: 'Multiple compliance issues including bank verification',
    documents: [{
      id: 1,
      name: 'Business Registration',
      type: 'Legal',
      uploadDate: '2023-05-22',
      expiryDate: null,
      status: 'Valid'
    }, {
      id: 2,
      name: 'Insurance Certificate',
      type: 'Insurance',
      uploadDate: '2023-05-22',
      expiryDate: '2023-09-22',
      status: 'Expired'
    }],
    bankVerification: {
      status: 'Warning',
      lastChecked: '2023-10-15',
      issues: [{
        severity: 'Medium',
        description: 'Account details partially match - manual verification required'
      }]
    }
  }, {
    id: 'VEN-2023-008',
    name: 'Stellar Logistics',
    status: 'Active',
    complianceStatus: 'Non-Compliant',
    type: 'Logistics',
    country: 'United States',
    onboardingDate: '2023-03-10',
    lastUpdated: '2023-10-10',
    issue: 'Sanctions list match found',
    documents: [{
      id: 1,
      name: 'Business License',
      type: 'Legal',
      uploadDate: '2023-03-10',
      expiryDate: '2024-03-10',
      status: 'Valid'
    }],
    bankVerification: {
      status: 'Verified',
      lastChecked: '2023-10-10',
      issues: []
    },
    sanctionsCheck: {
      status: 'Failed',
      lastChecked: '2023-10-10',
      issues: [{
        severity: 'Critical',
        description: 'Potential match found on international sanctions list'
      }]
    }
  }];
  const handleFileUpload = newFiles => {
    setFiles([...files, ...newFiles]);
  };
  const removeFile = index => {
    setFiles(files.filter((_, i) => i !== index));
  };
  const handleUpdateStatus = status => {
    if (!selectedVendor) return;
    // Process new documents if any
    const newDocuments = files.map((file, index) => ({
      id: selectedVendor.documents.length + index + 1,
      name: file.name,
      type: 'Compliance Document',
      uploadDate: new Date().toISOString().split('T')[0],
      expiryDate: null,
      status: 'Valid'
    }));
    // Update vendor with new compliance status and documents
    const updatedVendor = {
      ...selectedVendor,
      complianceStatus: status,
      documents: [...selectedVendor.documents, ...newDocuments],
      lastUpdated: new Date().toISOString().split('T')[0]
    };
    onUpdate(updatedVendor);
    setCurrentView('vendors');
  };
  const handleSendReminder = () => {
    // In a real app, this would send an email to the vendor
    alert(`Reminder sent to ${selectedVendor.name} to update their compliance documents.`);
  };
  const getComplianceBadge = status => {
    switch (status) {
      case 'Compliant':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800" data-id="element-19835">
            <CheckCircleIcon className="mr-1 h-3 w-3" data-id="element-19836" />
            {status}
          </span>;
      case 'Pending Review':
      case 'Incomplete':
      case 'Warning':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800" data-id="element-19837">
            <ClockIcon className="mr-1 h-3 w-3" data-id="element-19838" />
            {status}
          </span>;
      case 'Non-Compliant':
      case 'Expired':
      case 'Failed':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800" data-id="element-19839">
            <AlertTriangleIcon className="mr-1 h-3 w-3" data-id="element-19840" />
            {status}
          </span>;
      case 'Verified':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800" data-id="element-19841">
            <CheckCircleIcon className="mr-1 h-3 w-3" data-id="element-19842" />
            {status}
          </span>;
      case 'Not Verified':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800" data-id="element-19843">
            <ClockIcon className="mr-1 h-3 w-3" data-id="element-19844" />
            {status}
          </span>;
      default:
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800" data-id="element-19845">
            {status}
          </span>;
    }
  };
  const getDocumentStatusBadge = status => {
    switch (status) {
      case 'Valid':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800" data-id="element-19846">
            <CheckCircleIcon className="mr-1 h-3 w-3" data-id="element-19847" />
            {status}
          </span>;
      case 'Pending':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800" data-id="element-19848">
            <ClockIcon className="mr-1 h-3 w-3" data-id="element-19849" />
            {status}
          </span>;
      case 'Expired':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800" data-id="element-19850">
            <AlertTriangleIcon className="mr-1 h-3 w-3" data-id="element-19851" />
            {status}
          </span>;
      default:
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800" data-id="element-19852">
            {status}
          </span>;
    }
  };
  const getIssueIcon = issue => {
    if (issue.description.includes('bank') || issue.description.includes('account')) {
      return <CreditCardIcon className="h-5 w-5 mr-2 flex-shrink-0 mt-0.5 text-orange-500" data-id="element-19853" />;
    } else if (issue.description.includes('sanctions') || issue.description.includes('background')) {
      return <AlertCircleIcon className="h-5 w-5 mr-2 flex-shrink-0 mt-0.5 text-red-500" data-id="element-19854" />;
    } else if (issue.description.includes('expired')) {
      return <ClockIcon className="h-5 w-5 mr-2 flex-shrink-0 mt-0.5 text-yellow-500" data-id="element-19855" />;
    } else {
      return <AlertTriangleIcon className="h-5 w-5 mr-2 flex-shrink-0 mt-0.5 text-red-500" data-id="element-19856" />;
    }
  };
  const getSeverityClass = severity => {
    switch (severity) {
      case 'Critical':
        return 'text-red-600';
      case 'High':
        return 'text-orange-600';
      case 'Medium':
        return 'text-yellow-600';
      case 'Low':
        return 'text-blue-600';
      default:
        return 'text-gray-600';
    }
  };
  if (!selectedVendor) {
    return <div data-id="element-19857">
        <div className="mb-8 flex items-center" data-id="element-19858">
          <button onClick={() => setCurrentView('dashboard')} className="mr-4 text-gray-400 hover:text-gray-500" data-id="element-19859">
            <ArrowLeftIcon className="h-5 w-5" data-id="element-19860" />
          </button>
          <div data-id="element-19861">
            <h3 className="text-xl font-semibold text-gray-900" data-id="element-19862">
              Compliance Issues
            </h3>
            <p className="mt-1 text-sm text-gray-500" data-id="element-19863">
              Vendors requiring compliance review or updates
            </p>
          </div>
        </div>
        <div className="bg-white shadow overflow-hidden sm:rounded-lg" data-id="element-19864">
          <ul className="divide-y divide-gray-200" data-id="element-19865">
            {vendorsWithIssues.length === 0 ? <li className="px-6 py-4 text-center text-sm text-gray-500" data-id="element-19866">
                No compliance issues found.
              </li> : vendorsWithIssues.map(vendor => <li key={vendor.id} className="px-4 py-4 sm:px-6 hover:bg-gray-50 cursor-pointer" onClick={() => setSelectedVendor(vendor)} data-id="element-19867">
                  <div className="flex items-center justify-between" data-id="element-19868">
                    <div className="flex items-center" data-id="element-19869">
                      <div className="flex-shrink-0" data-id="element-19870">
                        {vendor.bankVerification && vendor.bankVerification.status === 'Failed' ? <CreditCardIcon className="h-5 w-5 text-orange-400" data-id="element-19871" /> : vendor.sanctionsCheck && vendor.sanctionsCheck.status === 'Failed' ? <ShieldIcon className="h-5 w-5 text-red-500" data-id="element-19872" /> : <AlertTriangleIcon className="h-5 w-5 text-yellow-400" data-id="element-19873" />}
                      </div>
                      <p className="ml-2 text-sm font-medium text-indigo-600 truncate" data-id="element-19874">
                        {vendor.name}
                      </p>
                    </div>
                    <div className="ml-2 flex-shrink-0" data-id="element-19875">
                      {getComplianceBadge(vendor.complianceStatus)}
                    </div>
                  </div>
                  <div className="mt-2 sm:flex sm:justify-between" data-id="element-19876">
                    <div className="sm:flex" data-id="element-19877">
                      <p className="flex items-center text-sm text-gray-500" data-id="element-19878">
                        {vendor.issue}
                      </p>
                    </div>
                    <div className="mt-2 flex items-center text-sm text-gray-500 sm:mt-0" data-id="element-19879">
                      <p data-id="element-19880">
                        Last updated:{' '}
                        <span className="ml-1.5" data-id="element-19881">{vendor.lastUpdated}</span>
                      </p>
                    </div>
                  </div>
                </li>)}
          </ul>
        </div>
      </div>;
  }
  return <div data-id="element-19882">
      <div className="mb-8 flex items-center" data-id="element-19883">
        <button onClick={() => setSelectedVendor(null)} className="mr-4 text-gray-400 hover:text-gray-500" data-id="element-19884">
          <ArrowLeftIcon className="h-5 w-5" data-id="element-19885" />
        </button>
        <div data-id="element-19886">
          <h3 className="text-xl font-semibold text-gray-900" data-id="element-19887">
            Compliance Review: {selectedVendor.name}
          </h3>
          <p className="mt-1 text-sm text-gray-500" data-id="element-19888">
            {selectedVendor.id} • Current Status:{' '}
            {getComplianceBadge(selectedVendor.complianceStatus)}
          </p>
        </div>
      </div>
      {/* Compliance Issues */}
      <div className="bg-white shadow overflow-hidden sm:rounded-lg mb-8" data-id="element-19889">
        <div className="px-4 py-5 sm:px-6" data-id="element-19890">
          <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-19891">
            Compliance Issues
          </h3>
          <p className="mt-1 text-sm text-gray-500" data-id="element-19892">
            Issues that need to be addressed
          </p>
        </div>
        <div className="border-t border-gray-200 px-4 py-5" data-id="element-19893">
          {selectedVendor.complianceStatus === 'Compliant' && (!selectedVendor.bankVerification || selectedVendor.bankVerification.status === 'Verified') && (!selectedVendor.sanctionsCheck || selectedVendor.sanctionsCheck.status !== 'Failed') ? <div className="text-sm text-green-600 flex items-center" data-id="element-19894">
              <CheckCircleIcon className="h-5 w-5 mr-2" data-id="element-19895" />
              This vendor is currently compliant with all requirements.
            </div> : <div className="space-y-4" data-id="element-19896">
              {/* Document issues */}
              {selectedVendor.documents && selectedVendor.documents.some(doc => doc.status === 'Expired') && <div className="text-sm text-red-600 flex items-start" data-id="element-19897">
                    <AlertTriangleIcon className="h-5 w-5 mr-2 flex-shrink-0 mt-0.5" data-id="element-19898" />
                    <div data-id="element-19899">
                      <p className="font-medium" data-id="element-19900">Expired Documents:</p>
                      <ul className="list-disc list-inside ml-4 mt-1" data-id="element-19901">
                        {selectedVendor.documents.filter(doc => doc.status === 'Expired').map(doc => <li key={doc.id} data-id="element-19902">
                              {doc.name} (Expired on {doc.expiryDate})
                            </li>)}
                      </ul>
                    </div>
                  </div>}
              {/* Missing documents */}
              {(!selectedVendor.documents || selectedVendor.documents.length === 0) && <div className="text-sm text-red-600 flex items-start" data-id="element-19903">
                  <AlertTriangleIcon className="h-5 w-5 mr-2 flex-shrink-0 mt-0.5" data-id="element-19904" />
                  <div data-id="element-19905">
                    <p className="font-medium" data-id="element-19906">Missing Required Documents:</p>
                    <p className="mt-1" data-id="element-19907">
                      This vendor has not uploaded any required documents.
                    </p>
                  </div>
                </div>}
              {/* Bank verification issues */}
              {selectedVendor.bankVerification && selectedVendor.bankVerification.issues && selectedVendor.bankVerification.issues.length > 0 && <div className="text-sm text-orange-600 flex items-start" data-id="element-19908">
                    <CreditCardIcon className="h-5 w-5 mr-2 flex-shrink-0 mt-0.5" data-id="element-19909" />
                    <div data-id="element-19910">
                      <p className="font-medium" data-id="element-19911">Bank Verification Issues:</p>
                      <ul className="list-disc list-inside ml-4 mt-1" data-id="element-19912">
                        {selectedVendor.bankVerification.issues.map((issue, index) => <li key={index} className={getSeverityClass(issue.severity)} data-id="element-19913">
                              {issue.description} ({issue.severity} severity)
                            </li>)}
                      </ul>
                      {selectedVendor.bankVerification.lastChecked && <p className="mt-1 text-xs text-gray-500" data-id="element-19914">
                          Last checked:{' '}
                          {selectedVendor.bankVerification.lastChecked}
                        </p>}
                    </div>
                  </div>}
              {/* Sanctions check issues */}
              {selectedVendor.sanctionsCheck && selectedVendor.sanctionsCheck.issues && selectedVendor.sanctionsCheck.issues.length > 0 && <div className="text-sm text-red-600 flex items-start" data-id="element-19915">
                    <ShieldIcon className="h-5 w-5 mr-2 flex-shrink-0 mt-0.5" data-id="element-19916" />
                    <div data-id="element-19917">
                      <p className="font-medium" data-id="element-19918">Sanctions Check Issues:</p>
                      <ul className="list-disc list-inside ml-4 mt-1" data-id="element-19919">
                        {selectedVendor.sanctionsCheck.issues.map((issue, index) => <li key={index} className={getSeverityClass(issue.severity)} data-id="element-19920">
                              {issue.description} ({issue.severity} severity)
                            </li>)}
                      </ul>
                      {selectedVendor.sanctionsCheck.lastChecked && <p className="mt-1 text-xs text-gray-500" data-id="element-19921">
                          Last checked:{' '}
                          {selectedVendor.sanctionsCheck.lastChecked}
                        </p>}
                    </div>
                  </div>}
              {/* Pending review status */}
              {selectedVendor.complianceStatus === 'Pending Review' && <div className="text-sm text-yellow-600 flex items-start" data-id="element-19922">
                  <ClockIcon className="h-5 w-5 mr-2 flex-shrink-0 mt-0.5" data-id="element-19923" />
                  <div data-id="element-19924">
                    <p className="font-medium" data-id="element-19925">Documents Pending Review:</p>
                    <p className="mt-1" data-id="element-19926">
                      Recently uploaded documents need to be reviewed.
                    </p>
                  </div>
                </div>}
            </div>}
        </div>
      </div>
      {/* Bank Account Verification */}
      {selectedVendor.bankVerification && <div className="bg-white shadow overflow-hidden sm:rounded-lg mb-8" data-id="element-19927">
          <div className="px-4 py-5 sm:px-6" data-id="element-19928">
            <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-19929">
              Bank Account Verification
            </h3>
            <p className="mt-1 text-sm text-gray-500" data-id="element-19930">
              External verification results from Eftsure
            </p>
          </div>
          <div className="border-t border-gray-200 px-4 py-5" data-id="element-19931">
            <div className="flex items-center justify-between mb-4" data-id="element-19932">
              <div className="flex items-center" data-id="element-19933">
                <CreditCardIcon className="h-5 w-5 text-gray-400 mr-2" data-id="element-19934" />
                <span className="text-sm font-medium text-gray-900" data-id="element-19935">
                  Verification Status:
                </span>
              </div>
              <div data-id="element-19936">
                {getComplianceBadge(selectedVendor.bankVerification.status)}
              </div>
            </div>
            {selectedVendor.bankVerification.lastChecked && <div className="text-sm text-gray-500 mb-4" data-id="element-19937">
                Last verified: {selectedVendor.bankVerification.lastChecked}
              </div>}
            {selectedVendor.bankVerification.issues && selectedVendor.bankVerification.issues.length > 0 ? <div className="mt-4" data-id="element-19938">
                <h4 className="text-sm font-medium text-gray-700 mb-2" data-id="element-19939">
                  Verification Issues:
                </h4>
                <ul className="space-y-2" data-id="element-19940">
                  {selectedVendor.bankVerification.issues.map((issue, index) => <li key={index} className={`flex items-start ${getSeverityClass(issue.severity)}`} data-id="element-19941">
                        {getIssueIcon(issue)}
                        <div data-id="element-19942">
                          <p className="font-medium" data-id="element-19943">
                            {issue.severity} Severity:
                          </p>
                          <p data-id="element-19944">{issue.description}</p>
                        </div>
                      </li>)}
                </ul>
              </div> : selectedVendor.bankVerification.status === 'Verified' ? <div className="text-sm text-green-600 flex items-center" data-id="element-19945">
                <CheckCircleIcon className="h-5 w-5 mr-2" data-id="element-19946" />
                Bank account details have been verified successfully with
                Eftsure.
              </div> : <div className="text-sm text-gray-500" data-id="element-19947">
                No verification has been performed yet.
              </div>}
            <div className="mt-4 flex justify-end" data-id="element-19948">
              <button type="button" className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-19949">
                Verify Bank Details
              </button>
            </div>
          </div>
        </div>}
      {/* Sanctions and Watchlist Check */}
      {selectedVendor.sanctionsCheck && <div className="bg-white shadow overflow-hidden sm:rounded-lg mb-8" data-id="element-19950">
          <div className="px-4 py-5 sm:px-6" data-id="element-19951">
            <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-19952">
              Sanctions & Watchlist Check
            </h3>
            <p className="mt-1 text-sm text-gray-500" data-id="element-19953">
              Results from international sanctions and watchlist screening
            </p>
          </div>
          <div className="border-t border-gray-200 px-4 py-5" data-id="element-19954">
            <div className="flex items-center justify-between mb-4" data-id="element-19955">
              <div className="flex items-center" data-id="element-19956">
                <ShieldIcon className="h-5 w-5 text-gray-400 mr-2" data-id="element-19957" />
                <span className="text-sm font-medium text-gray-900" data-id="element-19958">
                  Check Status:
                </span>
              </div>
              <div data-id="element-19959">
                {getComplianceBadge(selectedVendor.sanctionsCheck.status)}
              </div>
            </div>
            {selectedVendor.sanctionsCheck.lastChecked && <div className="text-sm text-gray-500 mb-4" data-id="element-19960">
                Last checked: {selectedVendor.sanctionsCheck.lastChecked}
              </div>}
            {selectedVendor.sanctionsCheck.issues && selectedVendor.sanctionsCheck.issues.length > 0 ? <div className="mt-4" data-id="element-19961">
                <h4 className="text-sm font-medium text-gray-700 mb-2" data-id="element-19962">
                  Detected Issues:
                </h4>
                <ul className="space-y-2" data-id="element-19963">
                  {selectedVendor.sanctionsCheck.issues.map((issue, index) => <li key={index} className={`flex items-start ${getSeverityClass(issue.severity)}`} data-id="element-19964">
                      {getIssueIcon(issue)}
                      <div data-id="element-19965">
                        <p className="font-medium" data-id="element-19966">
                          {issue.severity} Severity:
                        </p>
                        <p data-id="element-19967">{issue.description}</p>
                      </div>
                    </li>)}
                </ul>
              </div> : <div className="text-sm text-green-600 flex items-center" data-id="element-19968">
                <CheckCircleIcon className="h-5 w-5 mr-2" data-id="element-19969" />
                No sanctions or watchlist matches found.
              </div>}
            <div className="mt-4 flex justify-end" data-id="element-19970">
              <button type="button" className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-19971">
                Run New Check
              </button>
            </div>
          </div>
        </div>}
      {/* Documents */}
      <div className="bg-white shadow overflow-hidden sm:rounded-lg mb-8" data-id="element-19972">
        <div className="px-4 py-5 sm:px-6" data-id="element-19973">
          <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-19974">
            Documents
          </h3>
          <p className="mt-1 text-sm text-gray-500" data-id="element-19975">
            Review vendor documentation
          </p>
        </div>
        <div className="border-t border-gray-200" data-id="element-19976">
          {selectedVendor.documents && selectedVendor.documents.length > 0 ? <ul className="divide-y divide-gray-200" data-id="element-19977">
              {selectedVendor.documents.map(doc => <li key={doc.id} className="px-4 py-4" data-id="element-19978">
                  <div className="flex items-center justify-between" data-id="element-19979">
                    <div className="flex items-center" data-id="element-19980">
                      <FileTextIcon className="h-5 w-5 text-gray-400 mr-3" data-id="element-19981" />
                      <div data-id="element-19982">
                        <h4 className="text-base font-medium text-gray-900" data-id="element-19983">
                          {doc.name}
                        </h4>
                        <div className="mt-1 flex flex-wrap items-center text-sm text-gray-500" data-id="element-19984">
                          <span className="mr-3" data-id="element-19985">Type: {doc.type}</span>
                          <span className="mr-3" data-id="element-19986">
                            Uploaded: {doc.uploadDate}
                          </span>
                          {doc.expiryDate && <span className="mr-3" data-id="element-19987">
                              <CalendarIcon className="inline-block mr-1 h-4 w-4 text-gray-400" data-id="element-19988" />
                              Expires: {doc.expiryDate}
                            </span>}
                        </div>
                      </div>
                    </div>
                    <div data-id="element-19989">{getDocumentStatusBadge(doc.status)}</div>
                  </div>
                </li>)}
            </ul> : <div className="px-4 py-5 text-center text-sm text-gray-500" data-id="element-19990">
              No documents uploaded yet.
            </div>}
        </div>
      </div>
      {/* Upload New Documents */}
      <div className="bg-white shadow overflow-hidden sm:rounded-lg mb-8" data-id="element-19991">
        <div className="px-4 py-5 sm:px-6" data-id="element-19992">
          <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-19993">
            Upload New Documents
          </h3>
          <p className="mt-1 text-sm text-gray-500" data-id="element-19994">
            Add updated compliance documents
          </p>
        </div>
        <div className="border-t border-gray-200 px-4 py-5" data-id="element-19995">
          <FileUpload onFileUpload={handleFileUpload} data-id="element-19996" />
          {files.length > 0 && <div className="mt-4" data-id="element-19997">
              <h4 className="text-sm font-medium text-gray-700 mb-2" data-id="element-19998">
                New Files to Upload
              </h4>
              <ul className="border rounded-md divide-y divide-gray-200" data-id="element-19999">
                {files.map((file, index) => <li key={index} className="pl-3 pr-4 py-3 flex items-center justify-between text-sm" data-id="element-20000">
                    <div className="flex items-center" data-id="element-20001">
                      <FileTextIcon className="h-5 w-5 text-gray-400 mr-2" data-id="element-20002" />
                      <span className="ml-2 flex-1 w-0 truncate" data-id="element-20003">
                        {file.name}
                      </span>
                    </div>
                    <div className="flex items-center" data-id="element-20004">
                      <span className="text-xs text-gray-500 mr-4" data-id="element-20005">
                        {(file.size / 1024).toFixed(1)} KB
                      </span>
                      <button type="button" onClick={() => removeFile(index)} className="text-red-600 hover:text-red-900" data-id="element-20006">
                        <XIcon className="h-4 w-4" data-id="element-20007" />
                      </button>
                    </div>
                  </li>)}
              </ul>
            </div>}
        </div>
      </div>
      {/* Send Message */}
      <div className="bg-white shadow overflow-hidden sm:rounded-lg mb-8" data-id="element-20008">
        <div className="px-4 py-5 sm:px-6" data-id="element-20009">
          <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-20010">
            Send Message to Vendor
          </h3>
          <p className="mt-1 text-sm text-gray-500" data-id="element-20011">
            Request updates or provide information
          </p>
        </div>
        <div className="border-t border-gray-200 px-4 py-5" data-id="element-20012">
          <textarea rows={4} className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md p-2 border" placeholder="Enter message to vendor..." value={message} onChange={e => setMessage(e.target.value)} data-id="element-20013" />
          <div className="mt-3 flex justify-end" data-id="element-20014">
            <button type="button" onClick={handleSendReminder} className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" disabled={!message} data-id="element-20015">
              <SendIcon className="h-4 w-4 mr-1" data-id="element-20016" />
              Send Message
            </button>
          </div>
        </div>
      </div>
      {/* Update Compliance Status */}
      <div className="bg-white shadow overflow-hidden sm:rounded-lg mb-8" data-id="element-20017">
        <div className="px-4 py-5 sm:px-6" data-id="element-20018">
          <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-20019">
            Update Compliance Status
          </h3>
          <p className="mt-1 text-sm text-gray-500" data-id="element-20020">
            Change the vendor's compliance status
          </p>
        </div>
        <div className="border-t border-gray-200 px-4 py-5" data-id="element-20021">
          <div className="flex flex-wrap gap-3" data-id="element-20022">
            <button type="button" onClick={() => handleUpdateStatus('Compliant')} className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500" data-id="element-20023">
              <CheckIcon className="h-4 w-4 mr-1" data-id="element-20024" />
              Mark as Compliant
            </button>
            <button type="button" onClick={() => handleUpdateStatus('Pending Review')} className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-yellow-600 hover:bg-yellow-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500" data-id="element-20025">
              <ClockIcon className="h-4 w-4 mr-1" data-id="element-20026" />
              Mark as Pending Review
            </button>
            <button type="button" onClick={() => handleUpdateStatus('Non-Compliant')} className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500" data-id="element-20027">
              <XIcon className="h-4 w-4 mr-1" data-id="element-20028" />
              Mark as Non-Compliant
            </button>
          </div>
        </div>
      </div>
    </div>;
}